import {
  useCallback,
  useEffect,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import type { GAModalTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { openGAOnboarding } from '../betaGASharedState';
import GAModalImagePngSrc from './beta-ga-modal-image.png';

import styles from './GAWelcomeModalWithTour.less';

export const GAWelcomeModalWithTour: FunctionComponent<{
  onClose: () => void;
}> = ({ onClose }) => {
  const intl = useIntl();

  const onClickCloseButton = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'closeGAModalButton',
      source: 'welcomeToGAModal',
    });
    onClose();
  }, [onClose]);

  const onClickDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissWelcomeToGAModalButton',
      source: 'welcomeToGAModal',
    });
    onClose();
  }, [onClose]);

  const onClickContinue = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'takeATourButton',
      source: 'welcomeToGAModal',
    });
    onClose();
    openGAOnboarding();
  }, [onClose]);

  useEffect(() => {
    Analytics.sendViewedComponentEvent({
      componentType: 'modal',
      componentName: 'welcomeToGAModal',
      source: 'boardScreen',
    });
  }, []);

  return (
    <div className={styles.gaWelcomeModal}>
      <img className={styles.headingImage} src={GAModalImagePngSrc} alt="" />
      <DynamicButton
        className={styles.closeButton}
        iconBefore={<CloseIcon size="medium" />}
        onClick={onClickCloseButton}
        aria-label={intl.formatMessage({
          id: 'templates.dialog_close_button.close-dialog-label',
          defaultMessage: 'Close dialog',
          description: 'Aria label for the close dialog button',
        })}
        data-testid={getTestId<GAModalTestIds>('ga-modal-close-button')}
      />
      <div className={styles.contentContainer}>
        <FormattedMessage
          id="templates.beta_ga.welcome-to-the-new-trello"
          defaultMessage="Welcome to the new Trello!"
          description="GA modal content"
          tagName="h1"
        />
        <FormattedMessage
          id="templates.beta_ga.with-our-new-features"
          defaultMessage="With our new features like <strong>Inbox</strong> and <strong>Planner</strong>, you can capture to-dos from anywhere, organize them, and schedule time to get things done."
          description="GA modal content"
          tagName="p"
          values={{
            strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
          }}
        />
        <div className={styles.ctasContainer}>
          <Button appearance="subtle" onClick={onClickDismiss}>
            <FormattedMessage
              id="templates.beta_ga.ga-live-modal-dismiss-button"
              defaultMessage="Dismiss"
              description="GA modal dismiss button"
            />
          </Button>
          <Button appearance="primary" onClick={onClickContinue}>
            <FormattedMessage
              id="templates.beta_ga.take-a-quick-tour"
              defaultMessage="Take a quick tour"
              description="GA modal start tour button"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
